import { PageHeader } from "@/components/PageHeader";
import { Manifesto } from "@/components/home/Manifesto";
import { BrandMarquee } from "@/components/BrandMarquee";
import { CTASection } from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { MISSION, IMG } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";
import { Target, ShieldCheck, Flame, Users } from "lucide-react";

const VALUES = [
  { icon: Target, title: "High Standards", text: "We expect the athlete's best — and we give ours in return, every single session." },
  { icon: ShieldCheck, title: "Accountability", text: "Progress is earned. We hold athletes responsible for the process and the outcome." },
  { icon: Flame, title: "Individual Development", text: "No two athletes are the same. Every plan is built around one person's goals." },
  { icon: Users, title: "Autonomy", text: "We teach athletes to own their training, so they thrive long after they leave the facility." },
];

export default function About() {
  useSeo({
    title: "About | DST New Braunfels — Sports Performance, Baseball & Softball Training",
    description: "Learn about Dynamic Sports Training in New Braunfels, TX — our mission, values, and the coaches developing competitors ages 10–35.",
  });
  return (
    <>
      <PageHeader
        eyebrow="About DST"
        title={<>We Develop<br />Competitors.</>}
        subtitle="Dynamic Sports Training was founded on one belief: align the mind and body, and there's no ceiling on what an athlete can become."
        image={IMG.lifting}
      />

      <section className="bg-dst-ink py-24 lg:py-32 grain">
        <div className="container-dst relative z-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMG.batter} alt="Athlete training at DST" className="w-full h-[520px] object-cover" />
              <div className="absolute -bottom-6 -right-6 bg-dst-red p-6 max-w-[220px] hidden sm:block">
                <div className="font-oswald text-5xl font-700">10–35</div>
                <div className="text-white/80 text-sm tracking-wide uppercase mt-1">Ages we develop</div>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="eyebrow text-dst-red">Our Mission</span>
              <p className="mt-6 font-oswald text-3xl lg:text-4xl uppercase tracking-tight leading-snug">
                {MISSION}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-white/60 leading-relaxed text-lg">
                We serve youth, high school, and college athletes — baseball and softball players, and competitive athletes chasing more. We don't simply improve athletes. Through accountability, discipline, and relentless individual attention, we build the mindset and physical foundation of a true competitor.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
                <div><div className="font-oswald text-2xl text-dst-red">Ages 10–35</div><div className="text-white/50 text-sm uppercase tracking-wide">Youth · High School · College</div></div>
                <div><div className="font-oswald text-2xl text-dst-red">Baseball & Softball</div><div className="text-white/50 text-sm uppercase tracking-wide">Performance & Instruction</div></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <BrandMarquee theme="navy" />

      <section className="bg-dst-light text-dst-ink py-24 lg:py-32">
        <div className="container-dst">
          <Reveal className="mb-14 max-w-2xl">
            <span className="eyebrow text-dst-red">What Makes DST Different</span>
            <h2 className="mt-4 font-oswald font-700 uppercase tracking-tighter leading-[0.9] text-5xl lg:text-6xl text-dst-navy">
              Standards You Can Feel
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="bg-white p-8 h-full shadow-[0_20px_50px_-25px_rgba(0,34,68,0.3)] border-t-2 border-dst-red">
                  <v.icon size={32} className="text-dst-red mb-5" />
                  <h3 className="font-oswald uppercase text-2xl text-dst-navy mb-3">{v.title}</h3>
                  <p className="text-dst-ink/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Manifesto />
      <CTASection />
    </>
  );
}
